import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button, Select } from '../atoms';
import { PageHeader } from '../shell';
import { useToast } from '../organisms';
import { usePermissions } from '../lib/usePermissions';
import { useUserNames } from '../lib/useUserNames';
import { formatDate } from '../lib/orgTimezone';

/**
 * Tasks (ATA part 3 §8): the assigned work items behind the bell. A booked user sees their own; a
 * firm-wide role sees everyone's and may filter by person. Each row deep-links to the record it is
 * about and carries its deadline. Complete, reopen or dismiss from the row.
 */
export interface Task {
  id: string; kind: string; kind_label: string; title: string; body: string | null; status: 'open' | 'done' | 'dismissed';
  assignee_user_id: string; assignee_name: string | null; client_id: string | null; client_name: string | null;
  entity_type: string | null; entity_id: string | null; due_date: string | null; action_url: string | null; created_at: string; completed_at: string | null;
}

const FIRM_WIDE = new Set(['super_admin', 'admin', 'administrator']);
const isOverdue = (t: Task) => t.status === 'open' && !!t.due_date && t.due_date < new Date().toISOString().slice(0, 10);

export function Tasks() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const permissions = usePermissions();
  const role = permissions.data?.user.role ?? '';
  const me = permissions.data?.user.id ?? null;
  const firmWide = FIRM_WIDE.has(role);
  const users = useUserNames();
  const [status, setStatus] = useState<'open' | 'done' | 'all'>('open');
  const [kind, setKind] = useState('');
  const [assignee, setAssignee] = useState<string>('mine');

  const query = useQuery<{ rows: Task[] }>({
    queryKey: ['tasks', status, kind, firmWide ? assignee : 'mine', me],
    queryFn: ({ signal }) => {
      const q = new URLSearchParams({ status });
      if (kind) q.set('kind', kind);
      if (firmWide && assignee !== 'all') q.set('assignee_user_id', assignee === 'mine' ? (me ?? '') : assignee);
      return api.get<{ rows: Task[] }>(`/api/tasks?${q.toString()}`, signal);
    },
    enabled: !!permissions.data,
  });
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['tasks'] }); queryClient.invalidateQueries({ queryKey: ['tasks-dashboard'] }); };
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'complete' | 'reopen' | 'dismiss' }) => api.post(`/api/tasks/${id}/${action}`),
    onSuccess: invalidate,
    onError: (err) => toast({ variant: 'error', message: (err as Error).message.replace(/^HTTP \d+:\s*/, '') || 'Could not update the task' }),
  });

  const rows = query.data?.rows ?? [];
  const highlighted = params.get('task');
  const kinds = useMemo(() => Array.from(new Map(rows.map((r) => [r.kind, r.kind_label])).entries()).map(([value, label]) => ({ value, label })), [rows]);
  const openCount = rows.filter((r) => r.status === 'open').length;

  return (
    <>
      <PageHeader title="Tasks" subtitle={query.isLoading ? 'Loading…' : `${openCount} open${firmWide && assignee === 'all' ? ' across the firm' : ''}`} />
      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
        <Select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as 'open' | 'done' | 'all')} options={[{ value: 'open', label: 'Open' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All' }]} />
        <Select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} options={[{ value: '', label: 'Every kind' }, ...kinds]} />
        {firmWide && (
          <Select aria-label="Assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}
            options={[{ value: 'mine', label: 'My tasks' }, { value: 'all', label: 'Everyone' }, ...users.options.map((o) => ({ value: o.value, label: o.label }))]} />
        )}
      </div>
      {query.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading tasks…</p>
      ) : query.error ? (
        <p style={{ color: 'var(--color-status-red)' }}>Couldn’t load tasks.</p>
      ) : rows.length === 0 ? (
        <div style={{ padding: 'var(--sp-6)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center' }}>Nothing here. A deadline, a value notice or a year-roll reassignment will appear as a task when it is raised.</div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          {rows.map((t) => (
            <div key={t.id} data-testid="task-row" style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start', padding: 'var(--sp-3)', background: 'var(--color-card)', border: `1px solid ${t.id === highlighted ? 'var(--brand-primary)' : 'var(--color-border)'}`, borderRadius: 'var(--r-lg)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge variant="neutral">{t.kind_label}</Badge>
                  {t.status !== 'open' && <Badge variant={t.status === 'done' ? 'success' : 'neutral'}>{t.status === 'done' ? 'Done' : 'Dismissed'}</Badge>}
                  {isOverdue(t) && <Badge variant="danger">Overdue</Badge>}
                  <button type="button" onClick={() => t.action_url && navigate(t.action_url)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: t.action_url ? 'pointer' : 'default', font: 'inherit', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', textAlign: 'left' }}>{t.title}</button>
                </div>
                {t.body && <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>{t.body}</p>}
                <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                  {t.due_date ? `Due ${formatDate(t.due_date)}` : 'No due date'}{t.client_name ? ` · ${t.client_name}` : ''}{firmWide ? ` · ${t.assignee_name ?? '—'}` : ''}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--sp-1)', flexShrink: 0 }}>
                {t.status === 'open' ? (
                  <>
                    <Button variant="primary" size="sm" onClick={() => act.mutate({ id: t.id, action: 'complete' })}>Complete</Button>
                    <Button variant="ghost" size="sm" onClick={() => act.mutate({ id: t.id, action: 'dismiss' })}>Dismiss</Button>
                  </>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => act.mutate({ id: t.id, action: 'reopen' })}>Reopen</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
